import { FC, ReactNode } from 'react';

import dynamic from 'next/dynamic';

import { ChatV2Provider } from './ChatV2Provider';
import { ContextBridge } from './ContextBridge';
import { Namespace } from './useChatV2';

// Dynamically import ChatProvider to match the lazy-loading in sitecoreComponents.ts
const ChatProvider = dynamic(() => import('@apps/chat-shared-multilabel/ChatProvider').then(mod => mod.ChatProvider), {
  ssr: true,
});

interface ExperimentChatProviderProps {
  children: ReactNode;
  isEnabled: boolean;
  defaultNamespace?: Namespace;
  /**
   * Whether this provider runs inside an authenticated container.
   * Passed through to ChatV2Provider. Defaults to true.
   */
  authenticated?: boolean;
  /**
   * Optional URL to redirect to when the chatbot requests authentication.
   * Passed through to ChatV2Provider. When provided, a full-page redirect is
   * used instead of calling `signIn('okta')`.
   */
  loginRedirectUrl?: string;
}

/**
 * ExperimentChatProvider enables A/B testing between V1 (Seamly) and V2 (Custom) chatbot.
 *
 * When `isEnabled` is true, uses the new V2 chatbot with ContextBridge to maintain
 * backward compatibility with existing useChat() consumers.
 *
 * When `isEnabled` is false, uses the original V1 ChatProvider.
 *
 * @param isEnabled - Feature flag to enable V2 chatbot
 * @param defaultNamespace - Optional namespace override (e.g., 'oxxio-nl-b2c' for Oxxio containers)
 */
export const ExperimentChatProvider: FC<ExperimentChatProviderProps> = ({
  children,
  isEnabled,
  defaultNamespace,
  authenticated,
  loginRedirectUrl,
}) => {
  if (isEnabled) {
    return (
      <ChatV2Provider
        defaultNamespace={defaultNamespace}
        authenticated={authenticated}
        loginRedirectUrl={loginRedirectUrl}>
        <ContextBridge defaultNamespace={defaultNamespace}>{children}</ContextBridge>
      </ChatV2Provider>
    );
  }

  return <ChatProvider>{children}</ChatProvider>;
};
